// @flow
import * as React from 'react';
import classnames from 'classnames';
import { find } from 'lodash';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import axios from '../../../../../HttpRequests/axios';
import { calculateM2CO2, calculateStandardCO2 } from '../../../../../Utils/co2Calculator';

import {
  AlignLeftOutlined,
  CaretDownOutlined,
  CopyOutlined,
  DeleteOutlined,
  DragOutlined,
  PlusOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

import { Icon as LegacyIcon } from '@ant-design/compatible';
import { Menu, Dropdown, Tooltip, Popover, Select, Modal, Button, Form, Input, InputNumber } from 'antd';
import styles from './GroupItem.css';
import PopoverNotes from './PopoverNotes';

import type {
  EstimateItem,
  AddEstimateItemParams,
  UpdateEstimateItemFieldParams,
  RemoveEstimateItemParams,
  DuplicateEstimateItemParams
} from '../../../types';

/**
 * @typedef {Object} Props
 * @property {boolean} editable
 * @property {Array} selectedEstimateItemIds
 * @property {number} estimateGroupIndex
 * @property {number} estimateItemIndex
 * @property {Object} estimateItem
 * @property {Array} serviceTypes
 * @property {Function} onBlurEstimateItemField
 * @property {Function} onChangeEstimateItemField
 * @property {Function} onClickAddEstimateItem
 * @property {Function} onClickRemoveEstimateItem
 * @property {Function} onClickDuplicateEstimateItem
 * @property {Function} onClickInsertDescription
 * @property {Function} onToggleEstimateItem
 */

/**
 * @typedef {Object} State
 * @property {boolean} hoveredDragArea
 * @property {boolean} hoveredNotesButton
 * @property {boolean} hoveredActionButton
 * @property {string|null} focusedField
 * @property {Array<{value: string, label: string}>} paintOptions
 * @property {boolean} loadingPaintOptions
 */

class Component extends React.PureComponent {

  state = {
    hoveredDragArea: false,
    hoveredNotesButton: false,
    hoveredActionButton: false,
    focusedField: null,
    paintOptions: [],
    loadingPaintOptions: true,
    paintFetchError: false,
    paintFetchAttempted: false,
    lastAutoPaintDescription: null,
    // Custom paint modal state
    customPaintModalVisible: false,
    customPaintValues: {
      name: '',
      manufacturer: '',
      type: '',
      coverage: '',
      coats: '1',
      carbon: ''
    },
    // Coverage modal state for type D items with unit 'm'
    coverageModalVisible: false,
    coverageValues: {
      coverage: '',
      carbon: '',
      coats: '1'
    },
    lastAutoCoverageDescription: null
  }

  onChangeEstimateItemField = async (field, value, options = {}) => {
    const {
      estimateGroupIndex,
      estimateItemIndex,
      onChangeEstimateItemField
    } = this.props;

    await onChangeEstimateItemField({
      estimateGroupIndex,
      estimateItemIndex,
      field,
      value
    })

    // If this is a paint selection and we're changing the paint_id field
    if (field === 'paint_id' && !options.skipPaintDetails) {
      this.updatePaintDetails(value);
    }
  }

  onBlurEstimateItemField = async (field) => {

    const {
      estimateGroupIndex,
      estimateItemIndex,
      onBlurEstimateItemField
    } = this.props;

    await onBlurEstimateItemField({
      estimateGroupIndex,
      estimateItemIndex,
      field
    })
  }

  componentDidMount() {
    const { estimateItem } = this.props;
    if (estimateItem.type === 'D' && !this.state.paintFetchAttempted) {
      // Fetch paint options and then sync paint_id with material_id if it exists
      this.fetchPaintOptions().then(() => {
        if (estimateItem.material_id && this.state.paintOptions.length > 0) {
          this.syncPaintIdWithMaterialId();
        }

        // We'll only show the coverage modal when unit is changed to 'm' or when edit button is clicked
        // Not when the page initially loads
        // Coverage modal for new items will be handled in componentDidUpdate when unit changes

        // After paint options are loaded, check if the current description
        // might be an auto-generated one and store it
        if (estimateItem.description && estimateItem.paint_id) {
          // Store the current description as the last auto-generated one
          // This helps when switching between paints
          this.setState({ lastAutoPaintDescription: estimateItem.description });
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { estimateItem } = this.props;
    // Check if type changed to 'D' or if it's already 'D' but we haven't loaded options yet
    if ((prevProps.estimateItem.type !== estimateItem.type && estimateItem.type === 'D') ||
        (estimateItem.type === 'D' && this.state.paintOptions.length === 0 && !this.state.loadingPaintOptions && !this.state.paintFetchAttempted)) {
      this.fetchPaintOptions();
    }

    // If material_id changed and we have paint options loaded, sync the paint_id
    if (prevProps.estimateItem.material_id !== estimateItem.material_id &&
        this.state.paintOptions.length > 0 && estimateItem.type === 'D') {
      this.syncPaintIdWithMaterialId();
    }

    // Show coverage modal when unit changes to 'm' for type 'D' items
    if (estimateItem.type === 'D' &&
        prevProps.estimateItem.unit !== estimateItem.unit &&
        estimateItem.unit === 'm' &&
        !estimateItem.coverage_data) {
      console.log('Unit changed to m for type D item, showing coverage modal');
      this.showCoverageModal();
    }
  }

  // Synchronize paint_id with material_id when loading data
  syncPaintIdWithMaterialId = () => {
    const { estimateItem } = this.props;
    const { paintOptions } = this.state;

    // If we have a material_id or paint_id, find the correct paint option
    if (estimateItem.material_id || estimateItem.paint_id) {
      // Debug: Log the IDs and their types
      console.log('Looking for material_id:', estimateItem.material_id, 'Type:', typeof estimateItem.material_id);
      console.log('Looking for paint_id:', estimateItem.paint_id, 'Type:', typeof estimateItem.paint_id);
      console.log('Has paint_data:', !!estimateItem.paint_data);

      // Debug: Log all available paint options
      console.log('Available paint options:', paintOptions.map(opt => ({
        value: opt.value,
        valueType: typeof opt.value,
        material_id: opt.material_id,
        material_id_type: typeof opt.material_id,
        label: opt.label
      })));

      // Try to find a match using multiple ID fields
      const materialIdStr = estimateItem.material_id ? String(estimateItem.material_id) : null;
      const paintIdStr = estimateItem.paint_id ? String(estimateItem.paint_id) : null;

      // First try to find a match by paint_id if available
      let matchingOption = null;

      if (paintIdStr) {
        matchingOption = paintOptions.find(opt => String(opt.value) === paintIdStr);
        if (matchingOption) {
          console.log(`Found matching paint option by paint_id ${paintIdStr}:`, matchingOption.label);
        }
      }

      // If no match by paint_id, try by material_id
      if (!matchingOption && materialIdStr) {
        matchingOption = paintOptions.find(opt =>
          String(opt.material_id) === materialIdStr ||
          String(opt.value) === materialIdStr
        );
        if (matchingOption) {
          console.log(`Found matching paint option by material_id ${materialIdStr}:`, matchingOption.label);
        }
      }

      // If we have paint_data but no matching option, try to parse and use it
      if (!matchingOption && estimateItem.paint_data) {
        try {
          const paintData = JSON.parse(estimateItem.paint_data);
          console.log('Using stored paint_data:', paintData);

          // If we have paint data with an ID, try to find a match by that ID
          if (paintData && paintData.id) {
            const paintDataIdStr = String(paintData.id);
            matchingOption = paintOptions.find(opt =>
              String(opt.material_id) === paintDataIdStr ||
              String(opt.value) === paintDataIdStr
            );

            if (matchingOption) {
              console.log(`Found matching paint option by paint_data.id ${paintDataIdStr}:`, matchingOption.label);
            }
          }
        } catch (e) {
          console.error('Error parsing paint_data:', e);
        }
      }

      if (matchingOption) {
        console.log(`Found matching paint option:`, matchingOption.label);

        // Set the paint_id to the value of the matching option
        // Use skipPaintDetails option to prevent updating description during initial sync
        this.onChangeEstimateItemField('paint_id', matchingOption.value, { skipPaintDetails: true });

        // Store the paint data
        this.onChangeEstimateItemField('paint_data', JSON.stringify(matchingOption.originalItem));

        // Set material_id to ensure consistency
        // Handle case where originalItem might be undefined (for custom paint option)
        const materialId = matchingOption.material_id || (matchingOption.originalItem ? matchingOption.originalItem.id : '100000');
        this.onChangeEstimateItemField('material_id', materialId);

        // Calculate and set CO2 if we have the necessary values
        const quantity = parseFloat(estimateItem.quantity || 0);

        // Check if originalItem exists before accessing its properties
        // For custom paint option, originalItem might be undefined
        let coverage = 0;
        let carbon = 0;
        let coats = 1;

        if (matchingOption.originalItem) {
          coverage = parseFloat(matchingOption.originalItem.coverage || 0);
          carbon = parseFloat(matchingOption.originalItem.carbon || 0);
          coats = parseFloat(matchingOption.originalItem.coats || 1);
        } else if (estimateItem.paint_data) {
          // If no originalItem but we have paint_data, try to get values from there
          try {
            const paintData = JSON.parse(estimateItem.paint_data);
            coverage = parseFloat(paintData.coverage || paintData.Coverage || 0);
            carbon = parseFloat(paintData.carbon || paintData.Carbon || 0);
            coats = parseFloat(paintData.coats || paintData.Coats || 1);
          } catch (e) {
            console.error('Error parsing paint_data in syncPaintIdWithMaterialId:', e);
          }
        }
        if (quantity > 0 && carbon > 0 && estimateItem.unit === 'm2') {
          // Calculate CO2 for m2 using centralized function
          const co2Used = calculateM2CO2(quantity, carbon, coats);
          this.onChangeEstimateItemField('co2', co2Used.toFixed(2), { skipPaintDetails: true });
        }
      } else {
        console.log(`No matching paint option found for material_id ${materialIdStr} or paint_id ${paintIdStr}`);

        // Debug: Try to find a match manually and log results
        paintOptions.forEach(opt => {
          if (materialIdStr) {
            console.log(`Comparing: material_id=${opt.material_id}(${typeof opt.material_id}) with ${materialIdStr}(string)`,
                       `Match: ${String(opt.material_id) === materialIdStr}`);
          }
          if (paintIdStr) {
            console.log(`Comparing: value=${opt.value}(${typeof opt.value}) with ${paintIdStr}(string)`,
                       `Match: ${String(opt.value) === paintIdStr}`);
          }
        });
      }
    }
  }

  fetchPaintOptions = async () => {
    return new Promise(async (resolve) => {
      // Mark that we've attempted to fetch paint options
      this.setState({ paintFetchAttempted: true });
      
      try {
        // Fetch the paint settings using filter[token]=rams_paint_list
        const response = await axios.get('settings', {
          params: {
            'filter[token]': 'rams_paint_list',
            'page[size]': 1
          }
        });

        if (response.data && response.data.rows && response.data.rows.length > 0) {
          const paintSetting = response.data.rows[0];
          let paintOptions = [];

          try {
            // Validate that paintSetting.value exists and is not undefined
            if (!paintSetting || !paintSetting.value) {
              console.log('Paint setting value is undefined or null:', {
                paintSetting,
                hasValue: paintSetting ? 'value' in paintSetting : 'no paintSetting',
                valueType: paintSetting ? typeof paintSetting.value : 'N/A'
              });
              this.setState({ 
                paintOptions: [], 
                loadingPaintOptions: false,
                paintFetchError: true 
              }, () => resolve());
              return;
            }

            // Parse the JSON from the value field
            const paintData = JSON.parse(paintSetting.value);
            console.log('Raw paint data from settings:', paintData);

            if (Array.isArray(paintData)) {
              // Debug each paint item before mapping
              paintData.forEach((item, index) => {
                console.log(`Paint item ${index}:`, {
                  id: item.id,
                  idType: typeof item.id,
                  name: item.name
                });
              });

              // Map the data to options format for Select component
              // Use id as value and name as label, but preserve all original properties
              paintOptions = paintData.map(item => {
                // Ensure id is properly extracted and stored
                const id = item.id ? String(item.id) : `paint-${item.name}`;

                return {
                  value: id,
                  label: item.name,
                  name: item.name, // Store name separately for reference
                  // Store the material_id separately for direct access - ensure it's a string
                  material_id: item.id ? String(item.id) : null,
                  // Store the entire original item for later access
                  originalItem: item
                };
              });

              // Debug the mapped options
              console.log('Mapped paint options:', paintOptions.map(opt => ({
                value: opt.value,
                valueType: typeof opt.value,
                material_id: opt.material_id,
                material_id_type: typeof opt.material_id,
                label: opt.label
              })));

              // Add the 'Specify paint values' option at the end of the list
              paintOptions.push({
                value: '100000',
                label: 'Custom paint values',
                isCustomOption: true,
                material_id: '100000'
              });

              this.setState({ paintOptions, loadingPaintOptions: false }, () => {
                // After paint options are loaded, check if we need to set paint_id based on material_id
                this.syncPaintIdWithMaterialId();
                resolve();
              });
              return;
            }
          } catch (e) {
            console.error('Error parsing paint options:', e);
          }
        }

        // If we get here, either the response was empty or parsing failed
        // Don't show any paint options
        this.setState({
          paintOptions: [],
          loadingPaintOptions: false,
          paintFetchError: true
        }, () => resolve());
      } catch (error) {
        console.error('Error fetching paint options:', error);
        // Don't show any paint options on error
        this.setState({
          paintOptions: [],
          loadingPaintOptions: false,
          paintFetchError: true
        }, () => resolve());
      }
    });
  };

  // Method to update paint details in the description when a paint is actively selected
  updatePaintDetails = (paintId) => {
    const { estimateItem } = this.props;
    const { paintOptions, lastAutoPaintDescription } = this.state;

    // Check if the custom paint option is selected
    if (paintId === '100000') {
      // Show the custom paint modal
      this.showCustomPaintModal();
      return;
    }

    // Find the matching paint option by paint_id
    const matchingOption = paintOptions.find(opt => String(opt.value) === String(paintId));

    if (matchingOption) {
      // Extract paint details from the original item
      const paintItem = matchingOption.originalItem;
      const coverage = parseFloat(paintItem.coverage || 0);
      const carbon = parseFloat(paintItem.carbon || 0);
      const coats = paintItem.coats || '';
      const quantity = parseFloat(estimateItem.quantity || 0);

      // Add material_object field with the paint data
      const materialObject = JSON.stringify(paintItem);
      this.onChangeEstimateItemField('material_object', materialObject, { skipPaintDetails: true });

      // Get current description
      const currentDescription = estimateItem.description || '';

      // Check if the description is empty or matches the last auto-generated description
      const shouldUpdateDescription = !currentDescription ||
        (lastAutoPaintDescription && currentDescription === lastAutoPaintDescription);

      // If we should update the description, create a paint details string
      if (shouldUpdateDescription) {
        // Extract additional paint details
        const manufacturer = paintItem.manufacturer || '';
        const type = paintItem.type || '';
        const coatsNumber = paintItem.coats || '';

        // Start with the paint name
        let paintDetails = `${matchingOption.label}`;

        // Add manufacturer if available
        if (manufacturer) {
          paintDetails = `${manufacturer} ${paintDetails}`;
        }

        // Add type if available
        if (type) {
          paintDetails += ` (${type})`;
        }

        // Start details section
        let detailsSection = [];

        // Add coverage information if available
        if (coverage > 0) {
          detailsSection.push(`Coverage: ${coverage}m²/L`);
        }

        // Add coats information if available
        if (coatsNumber) {
          detailsSection.push(`Coats: ${coatsNumber}`);
        }

        // Add details section if we have any details
        if (detailsSection.length > 0) {
          paintDetails += ` - ${detailsSection.join(', ')}`;
        }

        // Add CO2 information if we can calculate it
        if (quantity > 0 && carbon > 0 && estimateItem.unit === 'm2') {
          // Get number of coats, default to 1 if not specified
          const coatsNumber = parseFloat(paintItem.coats || 1);

          // Calculate CO2 for m2 using centralized function
          const co2Used = calculateM2CO2(quantity, carbon, coatsNumber);

          // Add CO2 information to the description
          paintDetails += ` - CO2: ${co2Used.toFixed(2)}kg`;
        }

        // Store this as the last auto-generated description
        this.setState({ lastAutoPaintDescription: paintDetails });

        // Update the description field with the paint details
        //this.onChangeEstimateItemField('description', paintDetails, { skipPaintDetails: true });
      }
    }
  };

  // Show the custom paint modal
  showCustomPaintModal = () => {
    const { estimateItem } = this.props;
    let paintData = null;

    // Try to get paint data from multiple possible sources
    // First try paint_data field
    if (estimateItem.paint_data) {
      try {
        paintData = JSON.parse(estimateItem.paint_data);
        console.log('Loaded custom paint data from paint_data:', paintData);
      } catch (e) {
        console.error('Error parsing paint_data:', e);
      }
    }

    // If no paint data yet, try material_object field
    if (!paintData && estimateItem.material_object) {
      try {
        const materialObject = JSON.parse(estimateItem.material_object);
        // Check if this is a custom paint object
        if (this.isCustomPaintMaterialObject(materialObject)) {
          paintData = materialObject;
          console.log('Loaded custom paint data from material_object:', paintData);
        }
      } catch (e) {
        console.error('Error parsing material_object:', e);
      }
    }

    // If we found paint data, populate the form
    if (paintData) {
      this.setState({
        customPaintValues: {
          name: paintData.name || '',
          manufacturer: paintData.manufacturer || '',
          type: paintData.type || '',
          coverage: paintData.coverage || '',
          coats: paintData.coats || '1',
          carbon: paintData.carbon || ''
        }
      });
    } else {
      // Reset to default values if no data found
      this.setState({
        customPaintValues: {
          name: '',
          manufacturer: '',
          type: '',
          coverage: '',
          coats: '1',
          carbon: ''
        }
      });
    }

    this.setState({ customPaintModalVisible: true });
  };

  // Hide the custom paint modal
  hideCustomPaintModal = () => {
    this.setState({ customPaintModalVisible: false });
  };

  // Helper method to check if a material_object represents custom paint
  isCustomPaintMaterialObject = (materialObjectStr) => {
    try {
      if (!materialObjectStr) return false;

      const materialObject = typeof materialObjectStr === 'string'
        ? JSON.parse(materialObjectStr)
        : materialObjectStr;

      // Check if this is a custom paint object (id=100000)
      return materialObject && (String(materialObject.id) === '100000');
    } catch (e) {
      console.error('Error parsing material_object:', e);
      return false;
    }
  };

  // Show the coverage modal for linear meters
  showCoverageModal = () => {
    const { estimateItem } = this.props;

    // If we have existing coverage data, parse it
    if (estimateItem.coverage_data) {
      try {
        const coverageData = JSON.parse(estimateItem.coverage_data);
        this.setState({
          coverageValues: {
            coverage: coverageData.coverage || '',
            paint_coverage: coverageData.paint_coverage || 1,
            carbon: coverageData.carbon || '',
            coats: coverageData.coats || 1
          }
        });
      } catch (e) {
        console.error('Error parsing coverage data:', e);
        // Set default values if parsing fails
        this.setState({
          coverageValues: {
            coverage: '',
            paint_coverage: 1,
            carbon: '',
            coats: 1
          }
        });
      }
    } else {
      // Reset to default values if no data found
      this.setState({
        coverageValues: {
          coverage: '',
          paint_coverage: 1,
          carbon: '',
          coats: 1
        }
      });
    }

    this.setState({ coverageModalVisible: true });
  };

  // Hide the coverage modal
  hideCoverageModal = () => {
    this.setState({ coverageModalVisible: false });
  };

  // Handle coverage value changes in the modal
  handleCoverageValueChange = (field, value) => {
    this.setState(prevState => ({
      coverageValues: {
        ...prevState.coverageValues,
        [field]: value
      }
    }));
  };

  // Handle saving coverage values
  handleSaveCoverage = () => {
    const { estimateItem } = this.props;
    const { coverageValues } = this.state;

    // Create coverage data object
    const coverageData = {
      coverage: coverageValues.coverage,
      paint_coverage: coverageValues.paint_coverage || 1,
      carbon: coverageValues.carbon,
      coats: coverageValues.coats
    };

    // Convert to JSON string
    const coverageDataJson = JSON.stringify(coverageData);

    // Save coverage data
    this.onChangeEstimateItemField('coverage_data', coverageDataJson, { skipCoverageDetails: true });

    // Calculate CO2 if we have all required values
    const quantity = parseFloat(estimateItem.quantity || 0);
    const mCoverage = parseFloat(coverageValues.coverage || 0); // m²/m coverage
    const paintCoverage = parseFloat(coverageValues.paint_coverage || 1); // Default to 1 if not specified
    const carbon = parseFloat(coverageValues.carbon || 0);
    const coats = parseFloat(coverageValues.coats || 1);

    if (quantity > 0 && mCoverage > 0 && carbon > 0) {
      // Updated formula: quantity / (m coverage / paint coverage) * carbon * coats = co2Used
      const co2Used = quantity / (mCoverage / paintCoverage) * carbon * coats;
      console.log(`CO2 calculation for linear meters: ${quantity} / (${mCoverage} / ${paintCoverage}) * ${carbon} * ${coats} = ${co2Used}`);
      this.onChangeEstimateItemField('co2', co2Used.toFixed(2), { skipCoverageDetails: true });
    }

    // Update description to include coverage details
    let description = estimateItem.description || '';

    // Generate a new description with coverage details
    const newDescription = `Linear meters with coverage: ${coverageValues.coverage}m²/m, paint coverage: ${coverageValues.paint_coverage}m²/L, carbon: ${coverageValues.carbon}kg/m², coats: ${coverageValues.coats}`;

    // Only update description if it's empty or matches the last auto-generated description
    if (!description || description === this.state.lastAutoCoverageDescription) {
      this.setState({ lastAutoCoverageDescription: newDescription });
      this.onChangeEstimateItemField('description', newDescription, { skipCoverageDetails: true });
    }

    // Close the modal
    this.hideCoverageModal();
  };

  // Handle saving custom paint values
  handleSaveCustomPaint = () => {
    const { estimateItem } = this.props;
    const { customPaintValues } = this.state;

    // Create a paint data object
    const paintDataObject = {
      id: 100000, // Fixed ID for custom paint to ensure it can be saved
      name: customPaintValues.name,
      manufacturer: customPaintValues.manufacturer,
      type: customPaintValues.type,
      coverage: customPaintValues.coverage,
      coats: customPaintValues.coats,
      carbon: customPaintValues.carbon
    };

    // Create a JSON string of the paint data for storage
    const paintDataJson = JSON.stringify(paintDataObject);

    // Update the paint_data field
    this.onChangeEstimateItemField('paint_data', paintDataJson, { skipPaintDetails: true });

    // Set paint_id to 100000 for custom paint to ensure it can be saved
    this.onChangeEstimateItemField('paint_id', '100000', { skipPaintDetails: true });

    // Set material_id to 100000 for custom paint to ensure consistency
    this.onChangeEstimateItemField('material_id', '100000', { skipPaintDetails: true });

    // Add material_object field with the paint data
    this.onChangeEstimateItemField('material_object', paintDataJson, { skipPaintDetails: true });

    console.log('Custom paint saved with material_id: 100000');

    // Calculate CO2 if we have all required values
    const quantity = parseFloat(estimateItem.quantity || 0);
    const coverage = parseFloat(customPaintValues.coverage || 0);
    const carbon = parseFloat(customPaintValues.carbon || 0);
    const coats = parseFloat(customPaintValues.coats || 1);

    if (quantity > 0 && carbon > 0 && estimateItem.unit === 'm2') {
      // Calculate CO2 for m2 using centralized function
      const co2Used = calculateM2CO2(quantity, carbon, coats);
      this.onChangeEstimateItemField('co2', co2Used.toFixed(2), { skipPaintDetails: true });
    }

    // Update the description with custom paint details
    let newDescription = '';
    if (customPaintValues.manufacturer || customPaintValues.name) {
      newDescription = `${customPaintValues.manufacturer || ''} ${customPaintValues.name || ''}`;
    }
    if (customPaintValues.type) {
      newDescription += ` (${customPaintValues.type})`;
    }

    // Add coverage, coats and CO2 information
    let detailsSection = [];
    if (customPaintValues.coverage) {
      detailsSection.push(`Coverage: ${customPaintValues.coverage}m²/L`);
    }
    if (customPaintValues.coats) {
      detailsSection.push(`Coats: ${customPaintValues.coats}`);
    }

    // Add details section if we have any details
    if (detailsSection.length > 0) {
      newDescription += ` - ${detailsSection.join(', ')}`;
    }

    // Add CO2 information if we can calculate it
    if (quantity > 0 && carbon > 0) {
      const co2Used = estimateItem.unit === 'm2'
        ? calculateM2CO2(quantity, carbon, coats)
        : calculateStandardCO2(quantity, coverage, carbon, coats);
      newDescription += ` - CO2: ${co2Used.toFixed(2)}kg`;
    }

    // Only update description if it's empty or matches the last auto-generated one
    const currentDescription = estimateItem.description || '';
    const { lastAutoPaintDescription } = this.state;
    if (!currentDescription || currentDescription === lastAutoPaintDescription) {
      this.onChangeEstimateItemField('description', newDescription, { skipPaintDetails: true });
      // Store this as the last auto-generated description
      this.setState({ lastAutoPaintDescription: newDescription });
    }

    // Close the modal
    this.hideCustomPaintModal();
  };

  // Handle custom paint value changes
  handleCustomPaintValueChange = (field, value) => {
    this.setState(prevState => ({
      customPaintValues: {
        ...prevState.customPaintValues,
        [field]: value
      }
    }));
  };

  onClickInsertDescription = () => {

    const {
      estimateGroupIndex,
      estimateItemIndex,
      onClickInsertDescription
    } = this.props;

    onClickInsertDescription({
      estimateGroupIndex,
      estimateItemIndex
    })

  }

  onClickMenu = ({ key }: any) => {

    const {
      estimateGroupIndex,
      estimateItemIndex,
      onClickRemoveEstimateItem,
      onClickAddEstimateItem,
      onClickDuplicateEstimateItem
    } = this.props;

    if (key === 'remove') onClickRemoveEstimateItem({ estimateGroupIndex, estimateItemIndex });

    if (key === 'add') onClickAddEstimateItem({ estimateGroupIndex, estimateItemIndex });

    if (key === 'duplicate') onClickDuplicateEstimateItem({ estimateGroupIndex, estimateItemIndex });

  }

  render () {

    const {
      editable,
      estimateItem,
      serviceTypes,
      selectedEstimateItemIds,
      estimateGroupIndex,
      estimateItemIndex,
      onChangeEstimateItemField,
      onToggleEstimateItem
    } = this.props;

    const DragArea = SortableHandle(props => (
      <span {...props}>
        {props.children}
      </span>
    ));

    const ActionMenu = (

      <Menu onClick={this.onClickMenu}>

        <Menu.Item key="add">
          <PlusOutlined />
          {` Add`}
        </Menu.Item>

        <Menu.Item key="duplicate">
          <CopyOutlined />
          {` Duplicate`}
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item key="remove">
          <DeleteOutlined />
          {` Remove`}
        </Menu.Item>

      </Menu>

    )

    const serviceType = find(serviceTypes, type => type.key === estimateItem.type);

    const textOnly = estimateItem.type === 'TO'

    const showLineNumber = !textOnly && (!editable || !this.state.hoveredDragArea);

    const showDragIcon = editable && this.state.hoveredDragArea;

    return (
      <div className={styles.wrapper}>

        <DragArea
          className={styles.dragArea}
          style={{ cursor: editable ? 'grab' : 'default' }}
          onMouseEnter={() => editable && this.setState({ hoveredDragArea: true })}
          onMouseLeave={() => this.setState({ hoveredDragArea: false })}
        >

          {showLineNumber && (
            <div className={styles.lineNumber}>
              {estimateItem.line_number}
            </div>
          )}

          {showDragIcon && (
            <DragOutlined className={styles.dragIcon} />
          )}

        </DragArea>

        <div className={styles.table}>

          { editable && (
            <div className={classnames(styles.cell)} style={{ width: 30 }}>
              <input
                type="checkbox"
                checked={!!find(selectedEstimateItemIds, { estimateGroupIndex, estimateItemIndex })}
                onChange={() => onToggleEstimateItem({ estimateGroupIndex, estimateItemIndex })}
              />
            </div>
          )}

          <div className={classnames(styles.cell, { [styles.cellFocused] : this.state.focusedField === 'type' })} style={{ width: 45 }}>

            <Tooltip title={serviceType && serviceType.title} placement="right">

              <div>

                {serviceType && !textOnly && (
                  <div
                    className={styles.serviceTypeIcon}
                    style={{ backgroundColor: serviceType.color_light, color: serviceType.color_dark }}
                  >
                    {serviceType.key}
                  </div>
                )}

                {textOnly && (
                  <AlignLeftOutlined className={styles.textOnlyIcon} />
                )}

                {editable && (
                  <select
                    className={styles.select}
                    value={estimateItem.type || '__placeholder__'}
                    onChange={event => {
                      const val = event.target.value;
                      if (val === '__placeholder__') return;
                      this.onChangeEstimateItemField('type', val || null);
                    }}
                    onFocus={() => this.setState({ focusedField: 'type' })}
                    onBlur={() => this.setState({ focusedField: null })}
                    disabled={!editable}
                  >
                    <option value="__placeholder__" disabled hidden>
                      Select...
                    </option>
                    <optgroup label="Service Types">
                      {serviceTypes.filter(option => option.key !== 'TO').map(option => (
                        <option value={option.key} key={option.key}>
                          {option.title}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Other">
                      <option value="TO" key="text-only">
                        {"Text Only"}
                      </option>
                      <option value="none" key="none">
                        {"None"}
                      </option>
                    </optgroup>
                  </select>
                )}

              </div>

            </Tooltip>

          </div>

          <div className={classnames(styles.cell, styles.flexCell, { [styles.cellFocused] : this.state.focusedField === 'description' })}>
            {estimateItem.type === 'D' ? (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, position: 'relative', overflow: 'visible' }}>
                {this.state.loadingPaintOptions ? (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px' }}>
                    <LoadingOutlined style={{ marginRight: 8 }} /> Loading paint options...
                  </div>
                ) : (
                  <div>

                    <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', position: 'relative', overflow: 'visible', alignItems: 'center', height: '100%' }}>
                      {this.state.paintOptions.length > 0 && (
                        <Select
                          style={{
                            width: '30%',
                            minWidth: '200px',
                            height: '100%',
                            zIndex: 1000, // Ensure dropdown appears above other elements
                            position: 'relative',
                            overflow: 'visible'
                          }}
                          className={styles.fullHeightSelect}
                          dropdownStyle={{ zIndex: 1050 }}
                          value={estimateItem.paint_id || undefined}
                          onChange={value => {
                            // Update the paint_id field with the selected paint ID
                            this.onChangeEstimateItemField('paint_id', value);

                            // Check if custom paint option is selected (ID: 100000)
                            if (value === '100000') {
                              // For custom paint, explicitly set material_id to 100000
                              this.onChangeEstimateItemField('material_id', '100000');
                              console.log('Custom paint selected - setting material_id to 100000');
                              return; // updatePaintDetails will handle the rest
                            }

                            // Find the selected paint option to get all its data
                            const selectedOption = this.state.paintOptions.find(opt => opt.value === value);

                            if (selectedOption && selectedOption.originalItem) {
                              // Set material_id from the originalItem.id (or from the material_id property)
                              const materialId = selectedOption.material_id || selectedOption.originalItem.id;
                              this.onChangeEstimateItemField('material_id', materialId);

                              // Store all paint data in a new field paint_data as JSON string
                              this.onChangeEstimateItemField('paint_data', JSON.stringify(selectedOption.originalItem));

                              // Calculate and store CO2 if we have quantity, coverage and carbon values
                              // Use the current estimateItem from props to ensure we have the most up-to-date values
                              const { estimateItem } = this.props;
                              const quantity = parseFloat(estimateItem.quantity || 0);
                              const coverage = parseFloat(selectedOption.originalItem.coverage || 0);
                              const carbon = parseFloat(selectedOption.originalItem.carbon || 0);

                              console.log(`Calculating CO2 for paint change - Quantity: ${quantity}, Coverage: ${coverage}, Carbon: ${carbon}, Unit: ${estimateItem.unit}`);

                              if (quantity > 0 && coverage > 0 && carbon > 0 && estimateItem.unit === 'm2') {
                                // Get number of coats, default to 1 if not specified
                                const coats = parseFloat(selectedOption.originalItem.coats || 1);

                                // Calculate CO2 using centralized function
                                const co2Used = estimateItem.unit === 'm2'
                                  ? calculateM2CO2(quantity, carbon, coats)
                                  : calculateStandardCO2(quantity, coverage, carbon, coats);
                                this.onChangeEstimateItemField('co2', co2Used.toFixed(2));
                              } else {
                                console.log('Cannot calculate CO2: missing or invalid values');
                                this.onChangeEstimateItemField('co2', null);
                              }

                              console.log(`Setting material_id to ${materialId} for paint ${selectedOption.name}`);
                            } else if (value === undefined) {
                              // Clear the paint_data, co2, and material_id when selection is cleared
                              this.onChangeEstimateItemField('paint_data', null);
                              this.onChangeEstimateItemField('co2', null);
                              this.onChangeEstimateItemField('material_id', null);
                            }
                          }}
                          onFocus={() => this.setState({ focusedField: 'description' })}
                          disabled={!editable}
                          placeholder="Select paint"
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {this.state.paintOptions.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                      )}

                      <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
                        <textarea
                          className={styles.textarea}
                          value={estimateItem.description || ''}
                          onChange={event => this.onChangeEstimateItemField('description', event.target.value)}
                          onFocus={() => this.setState({ focusedField: 'description' })}
                          onBlur={() => this.setState({ focusedField: null })}
                          disabled={!editable}
                          style={{ flex: 1 }}
                          placeholder="Enter description"
                        />
                        {/* Show edit button if this is custom paint AND estimate is editable */}
                        {editable && (estimateItem.paint_data || estimateItem.material_object) &&
                         (estimateItem.paint_id === '100000' ||
                          estimateItem.material_id === '100000' ||
                          (estimateItem.material_object && this.isCustomPaintMaterialObject(estimateItem.material_object))) && (
                          <Button
                            type="link"
                            size="small"
                            icon={<LegacyIcon type="edit" />}
                            style={{ position: 'absolute', right: 5, top: 5, fontSize: '12px' }}
                            onClick={this.showCustomPaintModal}
                            title="Edit custom paint values"
                          />
                        )}

                        {/* Show edit button for coverage values if this is type D with unit 'm' AND estimate is editable */}
                        {editable && estimateItem.type === 'D' && estimateItem.unit === 'm' && (
                          <Button
                            type="link"
                            size="small"
                            icon={<LegacyIcon type="edit" />}
                            style={{ position: 'absolute', right: 5, top: 30, fontSize: '12px' }}
                            onClick={this.showCoverageModal}
                            title="Edit coverage values"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <textarea
                className={styles.textarea}
                value={estimateItem.description || ''}
                onChange={event => this.onChangeEstimateItemField('description', event.target.value)}
                onFocus={() => this.setState({ focusedField: 'description' })}
                onBlur={() => this.setState({ focusedField: null })}
                disabled={!editable}
                style={{ flex: 1 }}
              />
            )}
            {editable && (
              <div className={styles.insertIconWrapper}>
                <PlusOutlined onClick={this.onClickInsertDescription} />
              </div>
            )}
          </div>

          <div className={classnames(styles.cell, { [styles.cellFocused] : this.state.focusedField === 'quantity' })}>
            <input
              className={styles.input}
              value={!textOnly ? (estimateItem.quantity || '') : '-'}
              onChange={event => {
                const newQuantity = event.target.value;
                this.onChangeEstimateItemField('quantity', newQuantity);

                // Recalculate CO2 if this is a paint item with paint data
                if (estimateItem.type === 'D') {
                  // For unit 'm2' with paint data
                  if (estimateItem.unit === 'm2' && estimateItem.paint_data) {
                    try {
                      const paintData = JSON.parse(estimateItem.paint_data);
                      const quantity = parseFloat(newQuantity || 0);

                      // Handle both camelCase and lowercase property names for compatibility
                      let coverage = parseFloat(paintData.coverage || paintData.Coverage || 0);
                      let carbon = parseFloat(paintData.carbon || paintData.Carbon || 0);
                      let coats = parseFloat(paintData.coats || paintData.Coats || 1); // Default to 1 coat if not specified

                      // If we still don't have values, try alternative property names
                      if (coverage === 0) coverage = parseFloat(paintData['Coverage (m2 PL)'] || 0);
                      if (carbon === 0) carbon = parseFloat(paintData['Carbon (m2 PL)'] || 0);

                      console.log(`Quantity change CO2 calc - Coverage: ${coverage}, Carbon: ${carbon}, Coats: ${coats}, Quantity: ${quantity}`);

                      if (quantity > 0 && carbon > 0) {
                        // Calculate CO2 using centralized function
                        const co2Used = estimateItem.unit === 'm2'
                          ? calculateM2CO2(quantity, carbon, coats)
                          : calculateStandardCO2(quantity, coverage, carbon, coats);
                        this.onChangeEstimateItemField('co2', co2Used.toFixed(2), { skipPaintDetails: true });
                      } else {
                        this.onChangeEstimateItemField('co2', null, { skipPaintDetails: true });
                      }
                    } catch (e) {
                      console.error('Error calculating CO2:', e);
                    }
                  }
                  // For unit 'm' with coverage data
                  else if (estimateItem.unit === 'm' && estimateItem.coverage_data) {
                    try {
                      const coverageData = JSON.parse(estimateItem.coverage_data);
                      const quantity = parseFloat(newQuantity || 0);

                      const coverage = parseFloat(coverageData.coverage || 0);
                      const carbon = parseFloat(coverageData.carbon || 0);
                      const coats = parseFloat(coverageData.coats || 1); // Default to 1 coat if not specified

                      console.log(`Quantity change CO2 calc for linear meters - Coverage: ${coverage}, Carbon: ${carbon}, Coats: ${coats}, Quantity: ${quantity}`);

                      if (quantity > 0 && coverage > 0 && carbon > 0) {
                        // Calculate CO2 using centralized function
                        const co2Used = calculateStandardCO2(quantity, coverage, carbon, coats);
                        this.onChangeEstimateItemField('co2', co2Used.toFixed(2), { skipCoverageDetails: true });
                      } else {
                        this.onChangeEstimateItemField('co2', null, { skipCoverageDetails: true });
                      }
                    } catch (e) {
                      console.error('Error calculating CO2 for linear meters:', e);
                    }
                  }
                }
              }}
              onFocus={event => {
                event.target.select();
                this.setState({ focusedField: 'quantity' });
              }}
              onBlur={() => {
                this.onBlurEstimateItemField('quantity');
                this.setState({ focusedField: null })
              }}
              disabled={!editable || textOnly}
            />
          </div>

          <div className={classnames(styles.cell, { [styles.cellFocused] : this.state.focusedField === 'unit' })}>
            <div className={styles.outlet}>
              {!textOnly ? estimateItem.unit : '-'}
            </div>
            <select
              className={styles.select}
              value={estimateItem.unit}
              onChange={event => {
                const newUnit = event.target.value;
                this.onChangeEstimateItemField('unit', newUnit);

                // Update CO2 calculation when unit changes
                if (estimateItem.type === 'D') {
                  // If changing to 'm', show coverage modal to collect coverage data
                  if (newUnit === 'm') {
                    // Show coverage modal to collect coverage, carbon, and coats
                    this.showCoverageModal();
                  }
                  // If changing to m2 and we have paint data, calculate CO2
                  else if (newUnit === 'm2' && estimateItem.paint_data) {
                    try {
                      const paintData = JSON.parse(estimateItem.paint_data);
                      const quantity = parseFloat(estimateItem.quantity || 0);

                      // Handle both camelCase and lowercase property names for compatibility
                      let coverage = parseFloat(paintData.coverage || paintData.Coverage || 0);
                      let carbon = parseFloat(paintData.carbon || paintData.Carbon || 0);
                      let coats = parseFloat(paintData.coats || paintData.Coats || 1); // Default to 1 coat if not specified

                      // If we still don't have values, try alternative property names
                      if (coverage === 0) coverage = parseFloat(paintData['Coverage (m2 PL)'] || 0);
                      if (carbon === 0) carbon = parseFloat(paintData['Carbon (m2 PL)'] || 0);

                      console.log(`Unit change CO2 calc - Coverage: ${coverage}, Carbon: ${carbon}, Coats: ${coats}, Quantity: ${quantity}`);

                      if (quantity > 0 && carbon > 0) {
                        // Calculate CO2 using centralized function
                        const co2Used = estimateItem.unit === 'm2'
                          ? calculateM2CO2(quantity, carbon, coats)
                          : calculateStandardCO2(quantity, coverage, carbon, coats);
                        this.onChangeEstimateItemField('co2', co2Used.toFixed(2), { skipPaintDetails: true });
                      } else {
                        this.onChangeEstimateItemField('co2', null, { skipPaintDetails: true });
                      }
                    } catch (e) {
                      console.error('Error calculating CO2:', e);
                    }
                  } else {
                    // Not m2 unit, clear CO2
                    this.onChangeEstimateItemField('co2', null, { skipPaintDetails: true });
                  }
                }
              }}
              onFocus={() => this.setState({ focusedField: 'unit' })}
              onBlur={() => this.setState({ focusedField: null })}
              disabled={!editable || textOnly}
            >
              {['m','m2','Item','Hours','Days','No.','PSUM','NR'].map(option => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className={classnames(styles.cell, { [styles.cellFocused] : this.state.focusedField === 'rate' })}>
            <input
              className={styles.input}
              value={!textOnly ? (estimateItem.rate || '') : '-'}
              onChange={event => this.onChangeEstimateItemField('rate', event.target.value)}
              onFocus={event => {
                event.target.select();
                this.setState({ focusedField: 'rate' });
              }}
              onBlur={() => {
                this.onBlurEstimateItemField('rate');
                this.setState({ focusedField: null })
              }}
              disabled={!editable || textOnly}
            />
          </div>

          <div className={styles.cell}>
            <div className={classnames(styles.outlet, styles.readonly)}>
              {!textOnly ? estimateItem.subtotal : '-'}
            </div>
          </div>

          <div className={styles.cell}>
            <div className={classnames(styles.outlet, styles.readonly)}>
              {!textOnly ? estimateItem.discount : '-'}
            </div>
          </div>

          <div className={styles.cell}>
            <div className={classnames(styles.outlet, styles.readonly)}>
              {!textOnly ? estimateItem.total : '-'}
            </div>
          </div>

          <div className={classnames(styles.cell, { [styles.cellFocused] : this.state.focusedField === 'flag' })}>
            <div className={styles.outlet}>
              {!textOnly ? estimateItem.flag : '-'}
            </div>
            <select
              className={styles.select}
              value={estimateItem.flag || undefined}
              onChange={event => this.onChangeEstimateItemField('flag', event.target.value || null)}
              onFocus={() => this.setState({ focusedField: 'flag' })}
              onBlur={() => this.setState({ focusedField: null })}
              disabled={!editable || textOnly}
            >
              {[undefined, 'By Others', 'Omit', 'Included'].map((option) => (
                <option value={option} key={option || 'null'}>
                  {option}
                </option>
              ))}
            </select>
          </div>

        </div>

        <div className={styles.actionArea}>

          <span
            onMouseEnter={() => this.setState({ hoveredNotesButton: true })}
            onMouseLeave={() => this.setState({ hoveredNotesButton: false })}
          >
            <Popover
              title="Notes"
              trigger="click"
              content={(
                <PopoverNotes
                  onChangeEstimateItemField={onChangeEstimateItemField}
                  estimateGroupIndex={estimateGroupIndex}
                  estimateItemIndex={estimateItemIndex}
                  editable={editable}
                  note={estimateItem.notes}
                />
              )}
              style={{ minWidth: 320, maxWidth: 320 }}
            >

              {(estimateItem.notes || editable) && (
                <LegacyIcon
                  type="message"
                  theme={estimateItem.notes ? 'filled' : 'outlined'}
                  style={{ color: estimateItem.notes ? '#40a9ff' : ''}}
                  className={classnames(styles.notesButton, { [styles.notesButtonHovered]: this.state.hoveredNotesButton })}
                />
              )}

            </Popover>
          </span>

          {editable && (
            <span
              onMouseEnter={() => this.setState({ hoveredActionButton: true })}
              onMouseLeave={() => this.setState({ hoveredActionButton: false })}
            >
              <Dropdown overlay={ActionMenu} trigger={['click']}>
                <CaretDownOutlined
                  className={classnames(styles.actionButton, { [styles.actionButtonHovered]: this.state.hoveredActionButton })} />
              </Dropdown>
            </span>
          )}

        </div>

        {/* Custom Paint Values Modal */}
        <Modal
          title="Custom Paint Values"
          visible={this.state.customPaintModalVisible}
          onCancel={this.hideCustomPaintModal}
          footer={[
            <Button key="cancel" onClick={this.hideCustomPaintModal}>
              Cancel
            </Button>,
            <Button key="save" type="primary" onClick={this.handleSaveCustomPaint}>
              Save
            </Button>
          ]}
        >
          <Form layout="vertical">
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="Manufacturer" style={{ flex: 1 }}>
                <Input
                  value={this.state.customPaintValues.manufacturer}
                  onChange={e => this.handleCustomPaintValueChange('manufacturer', e.target.value)}
                  placeholder="Manufacturer"
                />
              </Form.Item>
              <Form.Item label="Paint Name" style={{ flex: 1 }}>
                <Input
                  value={this.state.customPaintValues.name}
                  onChange={e => this.handleCustomPaintValueChange('name', e.target.value)}
                  placeholder="Paint Name"
                />
              </Form.Item>
            </div>

            <Form.Item label="Type">
              <Input
                value={this.state.customPaintValues.type}
                onChange={e => this.handleCustomPaintValueChange('type', e.target.value)}
                placeholder="Type (e.g., Emulsion, Gloss)"
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="Coverage (m²/L)" style={{ flex: 1 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={this.state.customPaintValues.coverage ? parseFloat(this.state.customPaintValues.coverage) : null}
                  onChange={value => this.handleCustomPaintValueChange('coverage', value)}
                  placeholder="Coverage"
                  step={0.1}
                  min={0}
                />
              </Form.Item>
              <Form.Item label="Coats" style={{ flex: 1 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={this.state.customPaintValues.coats ? parseFloat(this.state.customPaintValues.coats) : 1}
                  onChange={value => this.handleCustomPaintValueChange('coats', value)}
                  placeholder="Coats"
                  min={1}
                  step={1}
                />
              </Form.Item>
            </div>

            <Form.Item label="Carbon (kg/m²)">
              <InputNumber
                style={{ width: '100%' }}
                value={this.state.customPaintValues.carbon ? parseFloat(this.state.customPaintValues.carbon) : null}
                onChange={value => this.handleCustomPaintValueChange('carbon', value)}
                placeholder="Carbon"
                step={0.01}
                min={0}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Coverage Modal for type D items with unit 'm' */}
        <Modal
          title="Coverage Values for Linear Meters"
          visible={this.state.coverageModalVisible}
          onCancel={this.hideCoverageModal}
          footer={[
            <Button key="cancel" onClick={this.hideCoverageModal}>
              Cancel
            </Button>,
            <Button key="save" type="primary" onClick={this.handleSaveCoverage}>
              Save
            </Button>
          ]}
        >
          <Form layout="vertical">
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="Coverage (m²/m)" style={{ flex: 1 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={this.state.coverageValues.coverage ? parseFloat(this.state.coverageValues.coverage) : null}
                  onChange={value => this.handleCoverageValueChange('coverage', value)}
                  placeholder="Coverage"
                  step={0.1}
                  min={0}
                />
              </Form.Item>
              <Form.Item label="Paint Coverage (m²/L)" style={{ flex: 1 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={this.state.coverageValues.paint_coverage ? parseFloat(this.state.coverageValues.paint_coverage) : 1}
                  onChange={value => this.handleCoverageValueChange('paint_coverage', value)}
                  placeholder="Paint Coverage"
                  step={0.1}
                  min={0.1}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="Carbon (kg/m²)" style={{ flex: 1 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={this.state.coverageValues.carbon ? parseFloat(this.state.coverageValues.carbon) : null}
                  onChange={value => this.handleCoverageValueChange('carbon', value)}
                  placeholder="Carbon"
                  step={0.01}
                  min={0}
                />
              </Form.Item>
              <Form.Item label="Coats" style={{ flex: 1 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={this.state.coverageValues.coats ? parseFloat(this.state.coverageValues.coats) : 1}
                  onChange={value => this.handleCoverageValueChange('coats', value)}
                  placeholder="Coats"
                  min={1}
                  step={1}
                />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    );
  }
}

export default SortableElement(Component);
